/**
 * Case Study archive: AJAX filters by industry and service with GSAP animations.
 * The other dropdown's options are updated from the response so no combination returns no results.
 * Only runs when .js-case-study-grid and .text-hero__filters exist.
 */
import gsap from "gsap";
import { initCaseStudyCardTags, revertCaseStudyCardTags } from "./case-study-card-tags.js";
import { initCaseStudyCardHoverVideo } from "./case-study-card-hover-video.js";

const OUT_DURATION = 0.25;
const IN_DURATION = 0.35;
const STAGGER_OUT = 0.03;
const STAGGER_IN = 0.04;
const IN_DELAY = 0.08;

const INDUSTRY_PLACEHOLDER = "By Industry";
const SERVICE_PLACEHOLDER = "By Service";

export function initCaseStudyFilters() {
	const grid = document.querySelector(".js-case-study-grid");
	const industrySelect = document.getElementById("case-study-filter-industry");
	const serviceSelect = document.getElementById("case-study-filter-service");

	if (!grid || !industrySelect || !serviceSelect) return;

	const ajaxUrl = typeof window.glaceV2 !== "undefined" && window.glaceV2.ajaxUrl
		? window.glaceV2.ajaxUrl
		: null;
	if (!ajaxUrl) return;

	function getParams() {
		return {
			action: "case_study_filter",
			industry: industrySelect.value || "",
			service: serviceSelect.value || "",
		};
	}

	function setSelectOptions(select, options, placeholder) {
		const valueBefore = select.value;
		select.innerHTML = "";
		const placeholderOpt = document.createElement("option");
		placeholderOpt.value = "";
		placeholderOpt.textContent = placeholder;
		select.appendChild(placeholderOpt);
		const slugs = [];
		if (Array.isArray(options)) {
			options.forEach((o) => {
				const opt = document.createElement("option");
				opt.value = o.slug;
				opt.textContent = o.name;
				select.appendChild(opt);
				slugs.push(o.slug);
			});
		}
		if (valueBefore && slugs.includes(valueBefore)) {
			select.value = valueBefore;
		} else if (valueBefore && !slugs.includes(valueBefore)) {
			select.value = "";
		}
	}

	function runFilter(refetchIfCleared = true) {
		const params = new URLSearchParams(getParams());
		const url = `${ajaxUrl}?${params.toString()}`;

		const items = grid.querySelectorAll(".grid-12__item");
		const hasItems = items.length > 0;

		const outComplete = () => {
			fetch(url, { method: "GET" })
				.then((res) => res.json())
				.then((data) => {
					if (!data.success || !data.data) return;
					const payload = data.data;

					if (typeof payload.html === "string") {
						revertCaseStudyCardTags(grid);
						grid.innerHTML = payload.html;
						initCaseStudyCardTags(grid);
						initCaseStudyCardHoverVideo(grid);
						const newItems = grid.querySelectorAll(".grid-12__item");
						const emptyMsg = grid.querySelector(".case-studies-grid__empty");
						if (newItems.length > 0) {
							gsap.fromTo(
								newItems,
								{ opacity: 0, y: 16 },
								{
									opacity: 1,
									y: 0,
									duration: IN_DURATION,
									stagger: STAGGER_IN,
									delay: IN_DELAY,
									ease: "power2.out",
								}
							);
						} else if (emptyMsg) {
							gsap.fromTo(emptyMsg, { opacity: 0 }, { opacity: 1, duration: IN_DURATION, delay: IN_DELAY });
						}
					}

					let industryCleared = false;
					let serviceCleared = false;
					if (Array.isArray(payload.industries)) {
						const before = industrySelect.value;
						setSelectOptions(industrySelect, payload.industries, INDUSTRY_PLACEHOLDER);
						if (before && industrySelect.value === "") industryCleared = true;
					}
					if (Array.isArray(payload.services)) {
						const before = serviceSelect.value;
						setSelectOptions(serviceSelect, payload.services, SERVICE_PLACEHOLDER);
						if (before && serviceSelect.value === "") serviceCleared = true;
					}

					if (refetchIfCleared && (industryCleared || serviceCleared)) {
						runFilter(false);
					}
				})
				.catch(() => {});
		};
		if (hasItems) {
			gsap.to(items, {
				opacity: 0,
				y: -12,
				duration: OUT_DURATION,
				stagger: STAGGER_OUT,
				ease: "power2.in",
				onComplete: outComplete,
			});
		} else {
			outComplete();
		}
	}

	industrySelect.addEventListener("change", () => runFilter(true));
	serviceSelect.addEventListener("change", () => runFilter(true));
}
